import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import { getBookingDrawerPaperSx } from './bookingDrawerLayout';
import {
  DASHBOARD_ACCENT,
  getDashboardDarkSurfaceTokens,
  getDashboardPageShellSx,
  getDashboardSectionContentPaperSx,
} from './dashboardSurfaceSx';

export const POS_ACCENT = DASHBOARD_ACCENT;

export function getPosThemeColors(isDark) {
  return getDashboardDarkSurfaceTokens(isDark);
}

export function getPosPageShellSx(themeMode) {
  return {
    ...getDashboardPageShellSx(themeMode),
    width: '100%',
    boxSizing: 'border-box',
  };
}

export function getPosConfigCardSx(isDark, extra = {}) {
  const tokens = getPosThemeColors(isDark);
  return {
    p: 3,
    height: '100%',
    borderRadius: 2,
    position: 'relative',
    overflow: 'hidden',
    background: isDark ? tokens.surfaceGradient : '#ffffff',
    backgroundColor: isDark ? tokens.surface : '#ffffff',
    border: `1px solid ${tokens.surfaceBorder}`,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: `linear-gradient(90deg, ${POS_ACCENT} 0%, ${alpha(POS_ACCENT, 0.45)} 100%)`,
    },
    ...extra,
  };
}

export function getPosDashboardCardSx(isDark, extra = {}) {
  return {
    ...getPosConfigCardSx(isDark, extra),
    '&::before': {
      display: 'none',
    },
  };
}

export function getPosMiniCardSx(isDark, accent = POS_ACCENT, extra = {}) {
  const tokens = getPosThemeColors(isDark);
  return {
    p: 1.2,
    borderRadius: 2,
    border: `1px solid ${isDark ? alpha(accent, 0.28) : alpha(accent, 0.22)}`,
    background: isDark
      ? `linear-gradient(150deg, ${alpha(accent, 0.1)} 0%, ${tokens.surface} 58%)`
      : `linear-gradient(150deg, ${alpha(accent, 0.08)} 0%, rgba(255,255,255,0.96) 58%)`,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    position: 'relative',
    overflow: 'hidden',
    ...extra,
  };
}

export function getPosHeroPaperSx(isDark) {
  const tokens = getPosThemeColors(isDark);
  return {
    px: { xs: 1.6, sm: 2, md: 2.35 },
    pt: { xs: 1.6, sm: 2, md: 2.35 },
    pb: { xs: 2, sm: 2.45, md: 2.85 },
    mb: 2.2,
    borderRadius: 3,
    border: `1px solid ${alpha(POS_ACCENT, isDark ? 0.34 : 0.22)}`,
    background: isDark
      ? `linear-gradient(145deg, ${alpha(POS_ACCENT, 0.12)} 0%, ${tokens.surface} 42%, ${alpha('#020617', 0.96)} 100%)`
      : `linear-gradient(145deg, ${alpha(POS_ACCENT, 0.1)} 0%, #ffffff 48%, ${alpha('#f0fdfa', 0.9)} 100%)`,
    backgroundColor: isDark ? tokens.surface : '#ffffff',
    boxShadow: isDark
      ? `0 18px 44px ${alpha('#000', 0.24)}, inset 0 1px 0 rgba(255,255,255,0.05)`
      : `0 16px 38px ${alpha('#0f766e', 0.1)}`,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      background: `radial-gradient(circle at 8% 18%, ${alpha(POS_ACCENT, isDark ? 0.22 : 0.14)} 0%, transparent 28%), radial-gradient(circle at 92% 0%, ${alpha('#38bdf8', isDark ? 0.16 : 0.1)} 0%, transparent 30%)`,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 3,
      background: `linear-gradient(90deg, ${POS_ACCENT} 0%, ${alpha('#38bdf8', 0.75)} 48%, ${alpha(POS_ACCENT, 0.25)} 100%)`,
    },
  };
}

export function getPosHeroContentSx(extra = {}) {
  return {
    flex: 1,
    minWidth: 0,
    boxSizing: 'border-box',
    ...extra,
  };
}

export function getPosHeroSubtitleSx(isDark) {
  const tokens = getPosThemeColors(isDark);
  return {
    color: tokens.subtleText,
    lineHeight: 1.55,
    display: 'block',
    mt: 0.45,
  };
}

export function getPosHeroChipSx(isDark, palette) {
  const { bg, color } = palette;
  return {
    height: 27,
    fontSize: '0.74rem',
    fontWeight: 800,
    borderRadius: '999px',
    bgcolor: isDark ? bg.dark : bg.light,
    color: isDark ? color.dark : color.light,
    border: `1px solid ${alpha(isDark ? color.dark : color.light, 0.18)}`,
    '& .MuiChip-label': { px: 1.05, py: 0 },
  };
}

export function getPosHeroChipRowSx() {
  return {
    mt: 0.9,
    pb: 0.85,
    minHeight: 28,
    alignItems: 'center',
  };
}

export function getPosTabsShellSx(isDark, extra = {}) {
  const tokens = getPosThemeColors(isDark);
  return {
    width: { xs: '100%', md: 'auto' },
    maxWidth: '100%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'stretch',
    borderRadius: 3,
    border: `1px solid ${alpha(POS_ACCENT, isDark ? 0.28 : 0.2)}`,
    background: isDark
      ? `linear-gradient(155deg, ${alpha(tokens.headerBg, 0.94)} 0%, ${alpha(POS_ACCENT, 0.07)} 100%)`
      : `linear-gradient(155deg, rgba(255,255,255,0.96) 0%, ${alpha('#ecfdf5', 0.88)} 100%)`,
    backgroundColor: isDark ? alpha(tokens.headerBg, 0.86) : alpha('#ffffff', 0.82),
    p: 0.75,
    boxSizing: 'border-box',
    backdropFilter: 'blur(14px)',
    boxShadow: isDark
      ? `inset 0 1px 0 rgba(255,255,255,0.06), 0 14px 32px ${alpha('#000', 0.22)}`
      : `inset 0 1px 0 rgba(255,255,255,0.95), 0 12px 28px ${alpha('#0f766e', 0.1)}`,
    position: 'relative',
    // Allow wrapped tab rows on mobile; clipping hid Table/Menu after Room.
    overflow: { xs: 'visible', md: 'hidden' },
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      background: `radial-gradient(circle at 100% 0%, ${alpha(POS_ACCENT, isDark ? 0.14 : 0.1)} 0%, transparent 42%)`,
    },
    ...extra,
  };
}

export function getPosMainTabsSx(isDark) {
  const tokens = getPosThemeColors(isDark);
  return {
    minHeight: 0,
    width: '100%',
    position: 'relative',
    zIndex: 1,
    '& .MuiTabs-scroller': {
      overflow: 'visible !important',
      height: 'auto',
    },
    '& .MuiTabs-flexContainer': {
      justifyContent: { xs: 'stretch', md: 'stretch' },
      alignItems: 'stretch',
      flexWrap: { xs: 'wrap', md: 'nowrap' },
      gap: 0.55,
      width: '100%',
    },
    '& .MuiTabs-scrollButtons': { display: 'none' },
    '& .MuiTabs-indicator': { display: 'none' },
    '& .MuiTab-root': {
      // Mobile: 3-per-row wrap so Dashboard/Orders/Room/Table/Menu all stay visible.
      flex: { xs: '1 1 calc(33.333% - 5px)', md: '1 1 0' },
      width: { xs: 'auto', md: 'auto' },
      minWidth: { xs: 0, md: 0 },
      maxWidth: { xs: 'none', md: 'none' },
      minHeight: { xs: 42, md: 46 },
      textTransform: 'none',
      fontWeight: 800,
      fontSize: { xs: '0.72rem', sm: '0.8125rem', md: '0.875rem' },
      letterSpacing: -0.01,
      borderRadius: 2.2,
      mx: 0,
      px: { xs: 0.65, sm: 1.1, md: 1.35 },
      py: { xs: 0.85, md: 1.05 },
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: { xs: 0.35, md: 0.65 },
      color: tokens.subtleText,
      whiteSpace: 'nowrap',
      transition: 'all 200ms ease',
      '& .MuiTab-iconWrapper': {
        fontSize: { xs: 16, md: 18 },
        mb: '0 !important',
        mr: '0 !important',
      },
      '&:hover': {
        color: POS_ACCENT,
        backgroundColor: alpha(POS_ACCENT, isDark ? 0.12 : 0.09),
        transform: 'translateY(-1px)',
      },
    },
    '& .Mui-selected': {
      color: `${POS_ACCENT} !important`,
      background: isDark
        ? `linear-gradient(140deg, ${alpha(POS_ACCENT, 0.24)} 0%, ${alpha(POS_ACCENT, 0.1)} 100%)`
        : `linear-gradient(140deg, ${alpha(POS_ACCENT, 0.18)} 0%, ${alpha(POS_ACCENT, 0.09)} 100%)`,
      boxShadow: isDark
        ? `0 10px 22px ${alpha(POS_ACCENT, 0.14)}, inset 0 0 0 1px ${alpha(POS_ACCENT, 0.36)}`
        : `0 10px 20px ${alpha(POS_ACCENT, 0.16)}, inset 0 0 0 1px ${alpha(POS_ACCENT, 0.26)}`,
    },
  };
}

export function getPosMenuSectionTabsSx(isDark) {
  const tokens = getPosThemeColors(isDark);
  return {
    flexGrow: 1,
    width: '100%',
    minHeight: 0,
    height: 'auto',
    position: 'relative',
    zIndex: 1,
    '& .MuiTabs-scroller': {
      overflow: 'visible !important',
      height: 'auto',
    },
    '& .MuiTabs-scrollButtons': { display: 'none' },
    '& .MuiTabs-indicator': { display: 'none' },
    '& .MuiTabs-flexContainer': {
      justifyContent: 'stretch',
      alignItems: 'stretch',
      flexWrap: { xs: 'wrap', sm: 'nowrap' },
      gap: 0.55,
      width: '100%',
    },
    '& .MuiTab-root': {
      flex: { xs: '1 1 calc(33.333% - 5px)', sm: '1 1 0' },
      width: { xs: 'auto', sm: 'auto' },
      minWidth: { xs: 0, sm: 0 },
      maxWidth: { xs: 'none', sm: 'none' },
      textTransform: 'none',
      fontWeight: 700,
      fontSize: { xs: '0.72rem', sm: '0.8125rem' },
      borderRadius: 2.2,
      minHeight: { xs: 42, sm: 46 },
      px: { xs: 0.65, sm: 1.35 },
      py: { xs: 0.85, sm: 1.05 },
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: tokens.subtleText,
      whiteSpace: 'nowrap',
      border: '1px solid transparent',
      transition: 'all 180ms ease',
      '&:hover': {
        color: POS_ACCENT,
        backgroundColor: alpha(POS_ACCENT, isDark ? 0.12 : 0.09),
        transform: 'translateY(-1px)',
      },
    },
    '& .Mui-selected': {
      color: `${POS_ACCENT} !important`,
      background: isDark
        ? `linear-gradient(140deg, ${alpha(POS_ACCENT, 0.24)} 0%, ${alpha(POS_ACCENT, 0.1)} 100%)`
        : `linear-gradient(140deg, ${alpha(POS_ACCENT, 0.18)} 0%, ${alpha(POS_ACCENT, 0.09)} 100%)`,
      boxShadow: isDark
        ? `0 10px 22px ${alpha(POS_ACCENT, 0.14)}, inset 0 0 0 1px ${alpha(POS_ACCENT, 0.36)}`
        : `0 10px 20px ${alpha(POS_ACCENT, 0.16)}, inset 0 0 0 1px ${alpha(POS_ACCENT, 0.26)}`,
    },
  };
}

export function getPosOrdersDataTableContainerSx(isDark) {
  const tokens = getPosThemeColors(isDark);
  return {
    p: { xs: 1.25, sm: 1.75 },
    '& .data-table-container': {
      mt: 0,
    },
    '& .data-table-container > .MuiBox-root:first-of-type': {
      px: 0.25,
      mb: 1.5,
    },
    '& .data-table': {
      borderCollapse: 'separate',
      borderSpacing: '0 10px',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      borderBottom: `1px solid ${tokens.surfaceBorder}`,
      pb: 1.25,
      backgroundColor: 'transparent',
    },
    '& .filter-row .MuiTableCell-root': {
      pt: 0.75,
      pb: 1.25,
      backgroundColor: 'transparent',
    },
    '& .MuiTableBody-root .data-table-row .MuiTableCell-root': {
      backgroundColor: isDark ? alpha(tokens.surface, 0.94) : '#ffffff',
      borderTop: `1px solid ${tokens.surfaceBorder}`,
      borderBottom: `1px solid ${tokens.surfaceBorder}`,
      py: 1.35,
      '&:first-of-type': {
        borderLeft: `1px solid ${tokens.surfaceBorder}`,
        borderTopLeftRadius: 12,
        borderBottomLeftRadius: 12,
      },
      '&:last-of-type': {
        borderRight: `1px solid ${tokens.surfaceBorder}`,
        borderTopRightRadius: 12,
        borderBottomRightRadius: 12,
      },
    },
    '& .MuiTableBody-root .data-table-row:hover .MuiTableCell-root': {
      backgroundColor: isDark ? alpha(POS_ACCENT, 0.08) : alpha(POS_ACCENT, 0.06),
    },
    '& .data-table-container > .MuiBox-root:last-of-type': {
      px: 0.25,
      pt: 0.5,
    },
  };
}

export function getPosPrimaryButtonSx(isDark, extra = {}) {
  return {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 700,
    backgroundColor: POS_ACCENT,
    color: '#04120e',
    boxShadow: isDark ? `0 8px 20px ${alpha(POS_ACCENT, 0.18)}` : undefined,
    '&:hover': {
      backgroundColor: POS_ACCENT,
      opacity: 0.92,
      boxShadow: isDark ? `0 8px 20px ${alpha(POS_ACCENT, 0.24)}` : undefined,
    },
    ...extra,
  };
}

export function getPosConfigHeaderIconSx(isDark) {
  return {
    width: 48,
    height: 48,
    borderRadius: '12px',
    background: alpha(POS_ACCENT, isDark ? 0.14 : 0.1),
    border: `1px solid ${alpha(POS_ACCENT, isDark ? 0.28 : 0.22)}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

export function getPosSectionDividerSx(isDark) {
  const tokens = getPosThemeColors(isDark);
  return {
    borderBottom: `1px solid ${tokens.surfaceBorder}`,
  };
}

export function getPosDataTablePaperSx(isDark, extra = {}) {
  return getPosConfigCardSx(isDark, { p: 0, ...extra });
}

const POS_ORDER_DRAWER_WIDTH_SX = {
  width: '100vw !important',
  maxWidth: '100vw !important',
  '@media (min-width:600px)': {
    width: '92vw !important',
    maxWidth: '92vw !important',
  },
  '@media (min-width:900px)': {
    width: '50vw !important',
    minWidth: '50vw !important',
    maxWidth: '50vw !important',
  },
};

export function getPosOrderDrawerPaperSx(isDark, extra = {}) {
  return getBookingDrawerPaperSx(isDark, {
    ...POS_ORDER_DRAWER_WIDTH_SX,
    ...extra,
  });
}

export function getPosOrderDrawerSectionPaperSx(isDark, extra = {}) {
  const tokens = getPosThemeColors(isDark);
  return {
    p: 1.2,
    mb: 1.5,
    borderRadius: 2,
    border: `1px solid ${tokens.surfaceBorder}`,
    backgroundColor: isDark ? alpha(tokens.surface, 0.35) : '#fafcff',
    ...extra,
  };
}

export function getPosOrderDrawerSummaryPaperSx(isDark, extra = {}) {
  const tokens = getPosThemeColors(isDark);
  return {
    p: 1.5,
    borderRadius: 2,
    border: `1px solid ${tokens.surfaceBorder}`,
    background: isDark ? tokens.surfaceGradient : 'linear-gradient(155deg, #ffffff 0%, #f8fbff 100%)',
    backgroundColor: isDark ? tokens.surface : '#ffffff',
    ...extra,
  };
}

export function getPosOrderDrawerToggleGroupSx(isDark) {
  const tokens = getPosThemeColors(isDark);
  return {
    flexWrap: 'wrap',
    gap: 0.8,
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      fontWeight: 700,
      borderColor: tokens.surfaceBorder,
      color: tokens.text,
      backgroundColor: isDark ? tokens.chipBg : '#ffffff',
      px: 1.45,
      py: 0.35,
      borderRadius: 1.1,
      minHeight: 34,
      '&:hover': {
        backgroundColor: isDark ? alpha(POS_ACCENT, 0.08) : alpha(POS_ACCENT, 0.06),
        borderColor: alpha(POS_ACCENT, isDark ? 0.35 : 0.3),
      },
    },
    '& .Mui-selected': {
      backgroundColor: `${alpha(POS_ACCENT, isDark ? 0.18 : 0.14)} !important`,
      color: `${isDark ? '#f8fafc' : '#0f172a'} !important`,
      borderColor: `${alpha(POS_ACCENT, isDark ? 0.55 : 0.45)} !important`,
      boxShadow: isDark
        ? `0 0 0 1px ${alpha(POS_ACCENT, 0.28)}, 0 8px 18px ${alpha(POS_ACCENT, 0.15)}`
        : `0 0 0 1px ${alpha(POS_ACCENT, 0.2)}, 0 8px 18px ${alpha(POS_ACCENT, 0.12)}`,
      '&:hover': {
        backgroundColor: `${alpha(POS_ACCENT, isDark ? 0.22 : 0.18)} !important`,
      },
    },
  };
}

export { getDashboardSectionContentPaperSx };
